import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Lock, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ItemOptions } from "./Cart";

export const Field = ({ label, children }) => (
  <label className="block space-y-2">
    <span className="text-sm font-extrabold text-white/80">{label}</span>
    {children}
  </label>
);

const cartPayload = (items) =>
  items.map((i) => ({ product_id: i.id, quantity: i.quantity, size: i.size || null, text: i.text || null, addons: i.addons || [], flavors: i.flavors || [] }));

const ADDR0 = { line1: "", line2: "", city: "", state: "", zip: "" };
const addrReady = (a) => a.line1.trim().length >= 3 && a.city.trim().length >= 2 && /^[A-Za-z]{2}$/.test(a.state) && /^\d{5}(-\d{4})?$/.test(a.zip);

function AddressFields({ addr, setAddr }) {
  const set = (k) => (e) => setAddr({ ...addr, [k]: k === "state" ? e.target.value.toUpperCase() : e.target.value });
  return (
    <div className="space-y-4" data-testid="checkout-address">
      <p className="flex items-center gap-2 font-display text-xl"><Truck className="h-5 w-5 text-[var(--blue-soft)]" /> Shipping address</p>
      <Field label="Street address"><input required className="field" value={addr.line1} onChange={set("line1")} autoComplete="address-line1" data-testid="checkout-address-line1-input" /></Field>
      <Field label="Apt, suite (optional)"><input className="field" value={addr.line2} onChange={set("line2")} autoComplete="address-line2" data-testid="checkout-address-line2-input" /></Field>
      <div className="grid grid-cols-[1fr_72px_110px] gap-3 max-sm:grid-cols-[1fr_1fr]">
        <div className="max-sm:col-span-2"><Field label="City"><input required className="field" value={addr.city} onChange={set("city")} autoComplete="address-level2" data-testid="checkout-city-input" /></Field></div>
        <Field label="State"><input required maxLength={2} placeholder="TX" className="field uppercase" value={addr.state} onChange={set("state")} autoComplete="address-level1" data-testid="checkout-state-input" /></Field>
        <Field label="ZIP"><input required inputMode="numeric" maxLength={10} className="field" value={addr.zip} onChange={set("zip")} autoComplete="postal-code" data-testid="checkout-zip-input" /></Field>
      </div>
    </div>
  );
}

function PostagePicker({ quote, rateId, setRateId, loading, onCalc, canCalc }) {
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="postage-picker">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-extrabold">Postage</p>
        <button type="button" onClick={onCalc} disabled={!canCalc || loading} className="btn-royal !px-4 !py-2 text-sm disabled:opacity-50" data-testid="calculate-postage-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />} {quote ? "Recalculate" : "Calculate postage"}
        </button>
      </div>
      {!quote && <p className="text-sm text-white/55">{canCalc ? "Tap Calculate postage to see mail delivery prices." : "Enter your full address to see postage prices."}</p>}
      {quote?.rates.map((r) => (
        <label key={r.id} className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 transition-colors ${rateId === r.id ? "border-[var(--pink)] bg-[#EC3D8C]/10" : "border-white/15 hover:border-[var(--blue-soft)]"}`} data-testid={`shipping-rate-${r.id}`}>
          <span className="flex items-center gap-3">
            <input type="radio" name="rate" checked={rateId === r.id} onChange={() => setRateId(r.id)} className="accent-[var(--pink)]" />
            <span><span className="block font-bold">{r.label}</span>{r.days && <span className="text-xs text-white/55">About {r.days} business day{r.days === 1 ? "" : "s"}</span>}</span>
          </span>
          <span className="font-extrabold text-[var(--pink)]">{money(r.amount_cents / 100)}</span>
        </label>
      ))}
      {quote && <p className="text-xs text-white/45">Mailed from Houston, TX 77084 · package weight about {quote.weight_lb} lb</p>}
    </div>
  );
}

export default function Checkout() {
  const { items, total } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [addr, setAddr] = useState(ADDR0);
  const [quote, setQuote] = useState(null);
  const [rateId, setRateId] = useState(null);
  const [needsShipping, setNeedsShipping] = useState(() => items.some((i) => i.shippable !== false));
  const hasLocal = items.some((i) => i.shippable === false);
  const [loadingRates, setLoadingRates] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setQuote(null); setRateId(null); }, [addr.zip, addr.line1, addr.city, addr.state, items]);
  if (!items.length) return <Navigate to="/cart" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const calc = async () => {
    setLoadingRates(true);
    try {
      const { data } = await api.post("/shipping/quote", { items: cartPayload(items), address: addr, name: form.name });
      setNeedsShipping(data.needs_shipping);
      if (data.needs_shipping) { setQuote(data); setRateId(data.rates[0]?.id || null); }
    } catch (err) { toast.error(errMsg(err)); }
    setLoadingRates(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (needsShipping && !rateId) return toast.error("Please calculate postage and choose a shipping option");
    setBusy(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: cartPayload(items), customer: form, address: addr,
        quote_id: quote?.quote_id || null, rate_id: rateId, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  const rate = quote?.rates.find((r) => r.id === rateId);
  const shippingAmt = rate ? rate.amount_cents / 100 : 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-14" data-testid="checkout-page">
      <h1 className="text-4xl sm:text-5xl">Checkout</h1>
      <div className="mt-6 grid gap-6 sm:mt-10 sm:gap-8 lg:grid-cols-[1fr_320px]">
        <form onSubmit={submit} className="panel space-y-5 p-5 sm:p-8" data-testid="checkout-form">
          <Field label="Full name"><input required className="field" value={form.name} onChange={set("name")} autoComplete="name" data-testid="checkout-name-input" /></Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Email"><input required type="email" inputMode="email" autoComplete="email" className="field" value={form.email} onChange={set("email")} data-testid="checkout-email-input" /></Field>
            <Field label="Phone"><input required type="tel" autoComplete="tel" minLength={7} className="field" value={form.phone} onChange={set("phone")} data-testid="checkout-phone-input" /></Field>
          </div>
          <AddressFields addr={addr} setAddr={setAddr} />
          {needsShipping && <PostagePicker quote={quote} rateId={rateId} setRateId={setRateId} loading={loadingRates} onCalc={calc} canCalc={addrReady(addr)} />}
          {hasLocal && (
            <p className="rounded-2xl bg-[#1F4FD8]/15 p-4 text-sm text-white/80" data-testid="local-delivery-note">
              {needsShipping ? "Fresh treats (like strawberry boxes) are delivered locally, and the rest of your order is mailed. We'll contact you to arrange the local delivery." : "Fresh treats in your cart are delivered locally. We'll contact you to arrange delivery."}
            </p>
          )}
          <Field label="Order note (optional)">
            <textarea rows={2} className="field" placeholder="e.g. gift message or delivery instructions" value={form.note} onChange={set("note")} data-testid="checkout-note-input" />
          </Field>
          <button disabled={busy} className="btn-candy w-full" data-testid="checkout-pay-button">
            <Lock className="h-4 w-4" /> {busy ? "Taking you to secure payment..." : `Pay ${money(total + shippingAmt)} securely`}
          </button>
          <p className="text-center text-xs text-white/50">You'll be redirected to Stripe's secure checkout page.</p>
        </form>
        <aside className="panel order-first h-fit space-y-3 p-5 sm:p-6 lg:order-none" data-testid="checkout-summary">
          <p className="font-display text-xl">Order summary</p>
          {items.map((i) => (
            <div key={i.key} className="flex justify-between gap-3 text-sm"><span className="text-white/75">{i.quantity} × {i.name}<ItemOptions item={i} /></span><span>{money(i.price * i.quantity)}</span></div>
          ))}
          <div className="ribbon-thin my-3 rounded-full" />
          <div className="flex justify-between text-sm"><span>Subtotal</span><span data-testid="checkout-subtotal">{money(total)}</span></div>
          <div className="flex justify-between text-sm"><span>Shipping</span><span data-testid="checkout-shipping">{needsShipping ? (rate ? money(shippingAmt) : "Calculated above") : "Local delivery"}</span></div>
          <div className="flex justify-between font-display text-2xl"><span>Total</span><span className="text-[var(--pink)]" data-testid="checkout-total">{money(total + shippingAmt)}</span></div>
          <p className="text-xs text-white/50">+ sales tax calculated by Stripe</p>
        </aside>
      </div>
    </div>
  );
}
